import React, { useMemo, useState, useEffect } from "react";

const T = {
  surface: "#FFFFFF", bgAlt: "#FFF8F8", border: "#FADDDD", borderMed: "#F5BABA",
  accent: "#E8001A", accentSoft: "#FFF0F0", ink: "#12100E",
  text: "#1A0005", textMid: "#5C1020", textDim: "#9A5060",
  pos: "#1A7A3A", neg: "#C42020", warn: "#B06000",
  shadow: "0 2px 8px rgba(232,0,26,0.08)",
};

/* One brand palette for the whole dashboard:
     KO / Coke → red,  PCI / Pepsi → blue,  Campa → violet */
export const BRAND = {
  ko:    { solid: "#E8001A", soft: "#FDE3E6", ink: "#A30012" },
  pci:   { solid: "#1F3C88", soft: "#E3E9F7", ink: "#1F3C88" },
  campa: { solid: "#7B4FC4", soft: "#EEE8F8", ink: "#5B2FA0" },
};
export const brandOf = (p) => {
  const s = String(p || "");
  if (/campa/i.test(s)) return "campa";
  if (/^\s*pc\b|pepsi|\bpci\b/i.test(s)) return "pci";
  return "ko";                              // KO / Coke / Coca-Cola packs
};
const BRAND_ORDER = { ko: 0, pci: 1, campa: 2 };
const isCampa = (p) => brandOf(p) === "campa";
const isKO    = (p) => brandOf(p) === "ko";

const fmtN = (v, d = 1) => (v == null || isNaN(v) ? "—" : Number(v).toFixed(d));
const fmtW = (v) => (v == null || isNaN(v) ? "—" : `${Math.round(v)}%`);
const fmtD = (v) => (v == null || isNaN(v) ? "—" : `${v >= 0 ? "+" : ""}${Math.round(v)}`);

/* Small inline bar so WD% reads at a glance */
function Bar({ value, max = 100, color }) {
  const pct = value == null ? 0 : Math.max(0, Math.min(1, value / max));
  return (
    <div style={{ width: 64, height: 10, background: "#EFEFEF", borderRadius: 3,
      overflow: "hidden", display: "inline-block", verticalAlign: "middle", marginRight: 7 }}>
      <div style={{ width: `${pct * 100}%`, height: "100%", background: color, borderRadius: 3 }} />
    </div>
  );
}

const Delta = ({ v }) => (
  <span style={{
    display: "inline-block", minWidth: 30, textAlign: "center",
    padding: "1px 5px", borderRadius: 8, fontSize: 10, fontWeight: 700,
    color: v == null ? T.textDim : v > 0 ? "#0F5C2A" : v < 0 ? "#8E1414" : T.textDim,
    background: v == null ? "#F2F2F2" : v > 0 ? "#D9F0E1" : v < 0 ? "#FBDADA" : "#F2F2F2",
  }}>{fmtD(v)}</span>
);

/**
 * bandLock — when the parent is on a single-band sheet ("Stores @ 10" or
 *   "Stores @ 20") it passes "10" / "20" and the table shows only that
 *   band's packs, with the dropdown hidden. "10+20" passes null, which
 *   shows everything.
 * embedded — drops the dark headline strip so the table sits inside the
 *   Outlet Coverage page as just another card.
 */
export default function WdTableView({ data, onAskAI, debug = [], bandLock = null, embedded = false }) {
  if (!data) return embedded ? null : <WdDiagnostic debug={debug} />;
  const { groups, rows, allIndia, period, deltaLabel, bands = [] } = data;

  const [bandSel, setBandSel] = useState(bandLock || "__all__");

  // Follow the parent when the sheet tab changes
  useEffect(() => { setBandSel(bandLock || "__all__"); }, [bandLock]);
  const [metricSel, setMetricSel] = useState("all");   // all | WD | VPO | IPS
  const [sortKey, setSortKey] = useState(null);        // {product, metric}
  const [sortDir, setSortDir] = useState("desc");

  // Bands keep their sheet order; inside a band the packs read KO → PCI → Campa
  const shownGroups = useMemo(() => {
    const bandIdx = {};
    groups.forEach((g, i) => { if (bandIdx[g.band] === undefined) bandIdx[g.band] = i; });
    return groups
      .filter(g => bandSel === "__all__" || String(g.band) === String(bandSel))
      .slice()
      .sort((a, b) =>
        (bandIdx[a.band] - bandIdx[b.band]) ||
        (BRAND_ORDER[brandOf(a.product)] - BRAND_ORDER[brandOf(b.product)]));
  }, [groups, bandSel]);

  /* "Leader" per row: within a price band, the pack with the highest VPO and
     the highest IPS is shown in green — the same treatment VPO/IPS gets. */
  const leaderOf = (rowValues, band, metric) => {
    let best = null, bestV = -Infinity;
    shownGroups.forEach(g => {
      if (g.band !== band) return;
      const v = rowValues?.[g.key]?.[metric];
      if (v != null && v > bestV) { bestV = v; best = g.key; }
    });
    return best;
  };

  const metricsFor = (g) => {
    const all = [
      { k: "WD",     label: "WD%",     head: period },
      { k: "WDd",    label: "",        head: deltaLabel },
      { k: "VPO",    label: "VPO",     head: period },
      { k: "IPS",    label: "IPS",     head: period },
      { k: "VPOIPS", label: "VPO/IPS", head: "" },
    ];
    if (metricSel === "all") return all;
    if (metricSel === "WD") return all.filter(m => m.k === "WD" || m.k === "WDd");
    return all.filter(m => m.k === metricSel);
  };

  const body = useMemo(() => {
    const list = rows.filter(r => !r.isTotal);
    if (!sortKey) return list;
    const { product, metric } = sortKey;
    return [...list].sort((a, b) => {
      const av = a.values?.[product]?.[metric];
      const bv = b.values?.[product]?.[metric];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === "desc" ? bv - av : av - bv;
    });
  }, [rows, sortKey, sortDir]);

  const toggleSort = (product, metric) => {
    if (sortKey && sortKey.product === product && sortKey.metric === metric) {
      setSortDir(d => (d === "desc" ? "asc" : "desc"));
    } else { setSortKey({ product, metric }); setSortDir("desc"); }
  };

  /* Headline: Campa vs KO WD gap at All India */
  const headline = useMemo(() => {
    if (!allIndia) return null;
    const campa = groups.filter(g => isCampa(g.product));
    const ko = groups.filter(g => isKO(g.product));
    const avg = (gs, m) => {
      const v = gs.map(g => allIndia.values?.[g.key]?.[m]).filter(x => x != null);
      return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null;
    };
    return { campaWD: avg(campa, "WD"), koWD: avg(ko, "WD"),
             campaVPO: avg(campa, "VPO"), koVPO: avg(ko, "VPO") };
  }, [groups, allIndia]);

  const green = { color: T.pos, fontWeight: 700 };
  const cell = (rec, m, g, row) => {
    const v = rec?.[m.k];
    if (m.k === "WDd") return <Delta v={v} />;
    if (m.k === "WD") {
      return (
        <span style={{ whiteSpace: "nowrap" }}>
          <Bar value={v} color={BRAND[brandOf(g.product)].solid} />
          <b style={{ fontSize: 11 }}>{fmtW(v)}</b>
        </span>
      );
    }
    if (m.k === "VPOIPS") {
      return <span style={v != null && v >= 1.5 ? green : { color: T.textMid }}>{fmtN(v)}</span>;
    }
    if (m.k === "VPO" || m.k === "IPS") {
      const lead = v != null && leaderOf(row?.values, g.band, m.k) === g.key;
      return <span style={lead ? green : undefined}>{fmtN(v)}</span>;
    }
    return fmtN(v);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14,
      padding: embedded ? 0 : 16 }}>
      {/* Headline strip — standalone tab only */}
      {!embedded && (
      <div style={{ background: T.ink, borderRadius: 10, padding: "14px 20px", color: "#fff",
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          {headline
            ? <>Campa WD {fmtW(headline.campaWD)} vs KO WD {fmtW(headline.koWD)} · Campa VPO {fmtN(headline.campaVPO)} vs KO VPO {fmtN(headline.koVPO)}</>
            : "Weighted distribution · volume per outlet · items per store"}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          {!bandLock && <>
          <span style={{ fontSize: 9.5, letterSpacing: 1, opacity: 0.6, textTransform: "uppercase" }}>Price band</span>
          <select value={bandSel} onChange={e => setBandSel(e.target.value)}
            style={{ background: "rgba(255,255,255,0.12)", color: "#fff",
              border: "1px solid rgba(255,255,255,0.25)", borderRadius: 6,
              padding: "4px 8px", fontSize: 11, cursor: "pointer" }}>
            <option value="__all__" style={{ color: "#000" }}>All</option>
            {bands.map(b => <option key={b} value={b} style={{ color: "#000" }}>@{b}</option>)}
          </select>
          </>}
          <span style={{ fontSize: 9.5, letterSpacing: 1, opacity: 0.6, textTransform: "uppercase" }}>Metric</span>
          <select value={metricSel} onChange={e => setMetricSel(e.target.value)}
            style={{ background: "rgba(255,255,255,0.12)", color: "#fff",
              border: "1px solid rgba(255,255,255,0.25)", borderRadius: 6,
              padding: "4px 8px", fontSize: 11, cursor: "pointer" }}>
            {[["all", "All"], ["WD", "WD% only"], ["VPO", "VPO only"], ["IPS", "IPS only"]]
              .map(([v, l]) => <option key={v} value={v} style={{ color: "#000" }}>{l}</option>)}
          </select>
          {onAskAI && <button onClick={onAskAI} style={{ background: T.accent, border: "none",
            color: "#fff", padding: "6px 12px", borderRadius: 8, fontSize: 11,
            fontWeight: 700, cursor: "pointer" }}>🤖 Ask AI</button>}
        </div>
      </div>
      )}

      {/* Table */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
        padding: 16, boxShadow: T.shadow }}>
        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <div style={{ color: T.text, fontSize: 15, fontWeight: 700 }}>
              Distribution &amp; Throughput by Market
              {bandLock ? ` — @${bandLock}` : ""}
            </div>
            <div style={{ color: T.textDim, fontSize: 10.5, marginTop: 2 }}>
              {period ? `${period} · ` : ""}{shownGroups.length} packs · {body.length} markets · click any header to sort
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {embedded && (
              <select value={metricSel} onChange={e => setMetricSel(e.target.value)}
                style={{ background: T.bgAlt, color: T.text, border: `1px solid ${T.border}`,
                  borderRadius: 6, padding: "3px 8px", fontSize: 10.5, cursor: "pointer" }}>
                {[["all", "All metrics"], ["WD", "WD% only"], ["VPO", "VPO only"], ["IPS", "IPS only"]]
                  .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            )}
            <div style={{ background: T.accentSoft, border: `1px solid ${T.borderMed}`,
              color: T.accent, fontSize: 9, padding: "3px 8px", borderRadius: 4,
              fontWeight: 700 }}>WD · VPO · IPS</div>
          </div>
        </div>

        <div style={{ overflowX: "auto", maxHeight: 620, overflowY: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
              {/* band row */}
              <tr>
                <th style={{ ...thBase, background: T.ink, color: "#fff",
                  textAlign: "left", position: "sticky", left: 0, zIndex: 3, width: "auto" }}
                  rowSpan={3}>Market</th>
                {shownGroups.map((g, i) => (
                  <th key={g.key} colSpan={metricsFor(g).length}
                    style={{ ...thBase, background: T.ink, color: "#fff", fontSize: 10,
                      borderLeft: i ? "2px solid rgba(255,255,255,0.25)" : "none" }}>
                    {g.band ? `@${g.band}` : ""}
                  </th>
                ))}
              </tr>
              {/* product row */}
              <tr>
                {shownGroups.map((g, i) => (
                  <th key={g.key} colSpan={metricsFor(g).length}
                    style={{ ...thBase,
                      background: BRAND[brandOf(g.product)].soft,
                      color: BRAND[brandOf(g.product)].ink, fontSize: 10,
                      borderLeft: i ? `2px solid ${T.borderMed}` : "none" }}>
                    {g.product}
                  </th>
                ))}
              </tr>
              {/* metric row */}
              <tr>
                {shownGroups.map((g, gi) => metricsFor(g).map((m, mi) => (
                  <th key={g.key + m.k}
                    onClick={() => m.k !== "WDd" && toggleSort(g.key, m.k)}
                    title={m.head}
                    style={{ ...thBase, background: T.bgAlt, color: T.textMid,
                      cursor: m.k === "WDd" ? "default" : "pointer",
                      borderLeft: (gi && !mi) ? `2px solid ${T.borderMed}` : "none",
                      borderBottom: `1px solid ${T.border}` }}>
                    {m.k === "WDd" ? deltaLabel : m.label}
                    {sortKey?.product === g.key && sortKey?.metric === m.k
                      ? (sortDir === "desc" ? " ▼" : " ▲") : ""}
                  </th>
                )))}
              </tr>
            </thead>
            <tbody>
              {allIndia && (
                <tr style={{ background: "#FFF4F4", fontWeight: 700 }}>
                  <td style={{ ...tdBase, textAlign: "left", fontWeight: 700,
                    position: "sticky", left: 0, background: "#FFF4F4", zIndex: 1 }}>
                    {allIndia.market}
                  </td>
                  {shownGroups.map((g, gi) => metricsFor(g).map((m, mi) => (
                    <td key={g.key + m.k} style={{ ...tdBase,
                      borderLeft: (gi && !mi) ? `2px solid ${T.borderMed}` : "none" }}>
                      {cell(allIndia.values?.[g.key], m, g, allIndia)}
                    </td>
                  )))}
                </tr>
              )}
              {body.map((r, ri) => (
                <tr key={r.market} style={{ background: ri % 2 ? "#FCFCFC" : "transparent" }}>
                  <td style={{ ...tdBase, textAlign: "left", fontWeight: 600,
                    position: "sticky", left: 0, zIndex: 1,
                    background: ri % 2 ? "#FCFCFC" : "#fff" }}>{r.market}</td>
                  {shownGroups.map((g, gi) => metricsFor(g).map((m, mi) => (
                    <td key={g.key + m.k} style={{ ...tdBase,
                      borderLeft: (gi && !mi) ? `2px solid ${T.borderMed}` : "none" }}>
                      {cell(r.values?.[g.key], m, g, r)}
                    </td>
                  )))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 10, fontSize: 9.5, color: T.textDim, display: "flex",
          flexWrap: "wrap", gap: 14 }}>
          <span><b style={{ color: T.textMid }}>WD%</b> weighted distribution</span>
          <span><b style={{ color: T.textMid }}>VPO</b> volume per outlet</span>
          <span><b style={{ color: T.textMid }}>IPS</b> items per store</span>
          <span><b style={{ color: T.textMid }}>VPO/IPS</b> computed, not read from sheet</span>
          <span style={{ marginLeft: "auto", fontStyle: "italic" }}>
            source sheet "{data.sheetName}"
          </span>
        </div>

        {/* Which spreadsheet column each metric resolved to — open this when a
            column reads empty, to see whether it was mapped at all. */}
        {data.columnMap?.length > 0 && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: "pointer", fontSize: 10, color: T.textDim, fontWeight: 600 }}>
              Column mapping ({data.columnMap.length} packs)
            </summary>
            <div style={{ marginTop: 6, fontSize: 10, fontFamily: "monospace",
              color: T.textMid, display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 3 }}>
              {data.columnMap.map(c => (
                <div key={c.pack}>
                  <b>@{c.band || "?"} {c.pack}</b>{" → "}
                  {["WD", "WDd", "VPO", "IPS"]
                    .map(k => `${k}:${c.cols[k] === undefined ? "—" : "col" + c.cols[k]}`)
                    .join("  ")}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

const thBase = { padding: "5px 8px", fontSize: 9.5, textTransform: "uppercase",
  letterSpacing: 0.4, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap", width: "1%" };
const tdBase = { padding: "3px 8px", borderBottom: `1px solid ${T.border}`,
  textAlign: "center", color: T.text, whiteSpace: "nowrap", width: "1%" };


/* Shown when no sheet parsed — names every sheet probed and the reason,
   so the fix is obvious instead of guesswork. */
function WdDiagnostic({ debug = [] }) {
  return (
    <div style={{ padding: 24 }}>
      <div style={{ background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 10, padding: 20, boxShadow: T.shadow }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
          No WD / VPO / IPS sheet could be read
        </div>
        <div style={{ fontSize: 11.5, color: T.textMid, marginTop: 6, lineHeight: 1.6 }}>
          The parser looks for a header row containing <b>WD% · VPO · IPS</b>, with
          pack names on the row above it and market names in the first text column.
          Leading blank columns and blank rows are fine.
        </div>
        {debug.length > 0 && (
          <table style={{ marginTop: 14, borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
            <thead>
              <tr style={{ background: T.bgAlt }}>
                <th style={{ ...thBase, textAlign: "left", width: "auto" }}>Sheet</th>
                <th style={{ ...thBase, textAlign: "left", width: "auto" }}>Why it was skipped</th>
              </tr>
            </thead>
            <tbody>
              {debug.map((d, i) => (
                <tr key={i}>
                  <td style={{ ...tdBase, textAlign: "left", width: "auto",
                    fontWeight: 600, fontFamily: "monospace" }}>{d.sheet}</td>
                  <td style={{ ...tdBase, textAlign: "left", width: "auto",
                    color: T.neg }}>{d.why}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}